import { createApiClient, getTimezoneOffsetString } from '../../../../../api/apiClient';
import { buildOrderRulesRequest } from './requestBuilder';
import {
  CrossDockLocationRow,
  OrderRuleCheckReviewResponse,
  OrderRuleCheckResponse,
  OrderRuleCheckRow,
  OrderRulesCheckBuildRow,
  OrderRulesCheckCrossdockRow,
  OrderRulesDashboardResponse,
  OrderRulesFilterParams,
} from './types';

// Raw record shape returned by /order-rules/orderrule-check — supports both
// nested `CrossDockLocations` array on each build record or legacy `CrossDockLocation`
// single objects.
interface RawOrderRuleCheckCrossDock {
  SalesLocationId?: string;
  'Sales Location'?: string;
  Materialnumber?: string;
  Package?: string;
  'Material Description'?: string;
  'Build Pallet'?: string | number;
  'Pack Spec'?: string | number;
  'Order Rule'?: string | number;
  ValuesAligned?: string;
  SKUEligible?: string;
  Flag?: string;
}

interface RawOrderRuleCheckRecord {
  BuildLocationId?: string;
  'Build Location'?: string;
  Materialnumber?: string;
  Package?: string;
  'Material Description'?: string;
  'Build Pallet'?: string | number;
  'Pack Spec'?: string | number;
  'Order Rule'?: string | number;
  ValuesAligned?: string;
  SKUEligible?: string;
  Flag?: string;
  CrossDockLocations?: RawOrderRuleCheckCrossDock[];
  CrossDockLocation?: RawOrderRuleCheckCrossDock;
}

interface RawOrderRuleCheckPayload {
  records?: RawOrderRuleCheckRecord[];
  totalElements?: number;
  totalPages?: number;
  page?: number;
  pageSize?: number;
}

const toNullableInt = (value: string | number | undefined): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isFlagged = (flag: string | undefined): boolean => String(flag ?? '').trim().toLowerCase() === 'review';

/**
 * Maps raw records into OrderRuleCheckRow[]. Handles both:
 * 1. `CrossDockLocations` array directly embedded inside each record.
 * 2. Grouping split records that carry a single `CrossDockLocation` object.
 */
const mapRawOrderRuleCheckPayload = (raw: RawOrderRuleCheckPayload): OrderRuleCheckResponse => {
  const records = raw.records ?? [];
  const groups = new Map<string, RawOrderRuleCheckRecord[]>();

  records.forEach((record) => {
    const key = `${record.BuildLocationId ?? ''}|${record.Materialnumber ?? ''}|${record.Package ?? ''}`;
    const group = groups.get(key);
    if (group) group.push(record);
    else groups.set(key, [record]);
  });

  const rows: OrderRuleCheckRow[] = Array.from(groups.values()).map((group) => {
    // Base row = record without single CrossDockLocation or the first record in the group
    const base = group.find((record) => !record.CrossDockLocation) ?? group[0];

    // Collect cross-dock locations from both the nested CrossDockLocations array and any split CrossDockLocation objects
    const directCrossDocks: RawOrderRuleCheckCrossDock[] = [];
    group.forEach((record) => {
      if (Array.isArray(record.CrossDockLocations)) {
        directCrossDocks.push(...record.CrossDockLocations);
      } else if (record.CrossDockLocation) {
        directCrossDocks.push(record.CrossDockLocation);
      }
    });

    const crossDockLocations: CrossDockLocationRow[] = directCrossDocks.map((xd) => ({
      buildLocation: base['Build Location'] ?? '',
      crossDockLocation: xd['Sales Location'] ?? xd.SalesLocationId ?? '',
      salesLocationId: xd.SalesLocationId,
      materialNumber: xd.Materialnumber ?? base.Materialnumber ?? '',
      packageName: xd.Package ?? base.Package ?? '',
      description: xd['Material Description'] ?? base['Material Description'] ?? '',
      orderRule: toNullableInt(xd['Order Rule']),
      eligible: xd.SKUEligible === 'Y',
      invoiced: true,
      reviewFlag: isFlagged(xd.Flag),
    }));

    return {
      location: base['Build Location'] ?? '',
      buildLocationId: base.BuildLocationId,
      materialNumber: base.Materialnumber ?? '',
      packageName: base.Package ?? '',
      description: base['Material Description'] ?? '',
      buildPallet: toNullableInt(base['Build Pallet']) ?? 0,
      packSpec: toNullableInt(base['Pack Spec']) ?? 0,
      orderRule: toNullableInt(base['Order Rule']),
      eligible: base.SKUEligible === 'Y',
      invoiced: true,
      reviewFlag: isFlagged(base.Flag),
      crossDockLocations,
    };
  });

  return {
    rows,
    totalElements: raw.totalElements,
    totalPages: raw.totalPages,
    page: raw.page,
    size: raw.pageSize,
  };
};

// ─── API: /api/order-rules/orderrule-check/review ────────────────────────────

/** Toggles the review status for a build (or cross-dock) row; the backend flips reviewed Y↔N on each call. */
export const updateOrderRuleCheckReview = async (
  buildLocationId: string,
  materialNumber: string,
  salesLocationId?: string,
): Promise<OrderRuleCheckReviewResponse> => {
  const api = createApiClient();
  const body: Record<string, string> = { buildLocationId, materialNumber };
  if (salesLocationId) body.salesLocationId = salesLocationId;
  // clientTimestamp is added automatically by the apiClient interceptor for POST requests; only clientTimezone needs to be added here.
  const res = await api.post<OrderRuleCheckReviewResponse>('/order-rules/orderrule-check/review', body, {
    params: { clientTimezone: getTimezoneOffsetString() },
  });
  return res.data;
};

// ─── API: /api/order-rules/orderrule-check ───────────────────────────────────

export const fetchOrderRulesCheck = async (
  params: OrderRulesFilterParams,
  locationIds: string[],
  pagination: { page: number; size: number },
  options?: { showNonEligibleSkus?: boolean; showReviewed?: boolean; showNonInvoicedSkus?: boolean },
): Promise<OrderRuleCheckResponse> => {
  const api = createApiClient();
  const body = buildOrderRulesRequest(params, locationIds, pagination);
  if (options?.showNonEligibleSkus) body.showNonEligibleSkus = true;
  if (options?.showReviewed) body.showReviewed = true;
  if (options?.showNonInvoicedSkus) body.showNonInvoicedSkus = true;
  const res = await api.post<{ data: RawOrderRuleCheckPayload }>('/order-rules/orderrule-check', body);
  const raw = (res.data as any)?.data ?? res.data;
  return mapRawOrderRuleCheckPayload(raw as RawOrderRuleCheckPayload);
};

// ─── Normalizer: backend → legacy dashboard shape ────────────────────────────

export const normalizeOrderRuleCheckResponse = (
  data: OrderRuleCheckResponse,
): Pick<OrderRulesDashboardResponse, 'orderRulesCheckBuild' | 'orderRulesCheckCrossdock'> => {
  const buildRows: OrderRulesCheckBuildRow[] = [];
  const crossdockRows: OrderRulesCheckCrossdockRow[] = [];

  (data.rows ?? []).forEach((row) => {
    buildRows.push({
      location: row.location,
      buildLocationId: row.buildLocationId,
      materialNumber: row.materialNumber,
      packageName: row.packageName,
      description: row.description,
      buildPallet: row.buildPallet,
      packSpec: row.packSpec,
      orderRule: row.orderRule,
      eligible: row.eligible,
      invoiced: row.invoiced,
      reviewFlag: row.reviewFlag,
    });
    (row.crossDockLocations ?? []).forEach((xd) => {
      crossdockRows.push({
        buildLocation: xd.buildLocation,
        crossDockLocation: xd.crossDockLocation,
        salesLocationId: xd.salesLocationId,
        materialNumber: xd.materialNumber,
        packageName: xd.packageName,
        description: xd.description,
        orderRule: xd.orderRule,
        eligible: xd.eligible,
        invoiced: xd.invoiced,
        reviewFlag: xd.reviewFlag,
      });
    });
  });

  return { orderRulesCheckBuild: buildRows, orderRulesCheckCrossdock: crossdockRows };
};
